/**
 * DevLint API Client
 *
 * Production-ready HTTP client with:
 * - Typed generic request methods (get, post, patch, delete)
 * - Automatic Authorization header injection
 * - Token refresh on 401
 * - Structured error handling
 */

// ── Constants ───────────────────────────────────────────────────────────
const VITE_API_URL = import.meta.env.VITE_API_URL;

if (!VITE_API_URL) {
  console.warn('[DevLint] VITE_API_URL no está definida. Usando el origen actual como fallback.');
}

const API_BASE_URL = VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
const API_PREFIX = '/api/v1';

console.info(`[DevLint] API Client initialized connecting to: ${API_BASE_URL}${API_PREFIX}`);

const TOKEN_KEY = 'devlint_access_token';
const REFRESH_TOKEN_KEY = 'devlint_refresh_token';

// ── Token helpers ───────────────────────────────────────────────────────
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// ── Error class ─────────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
    message?: string,
  ) {
    super(message ?? `API Error ${status}`);
    this.name = 'ApiError';
  }
}

// ── Types ───────────────────────────────────────────────────────────────
export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number>;
  /** If true, skip JSON serialization (e.g. for FormData) */
  raw?: boolean;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// ── Internal state ──────────────────────────────────────────────────────
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// ── Core fetch wrapper ──────────────────────────────────────────────────

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const url = `${API_BASE_URL}${API_PREFIX}/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`;
    const res = await fetch(url, { method: 'POST' });
    if (!res.ok) return false;

    const data: TokenResponse = await res.json();
    tokenStorage.setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { headers = {}, params, raw = false } = options;

  // Build URL
  let url = `${API_BASE_URL}${API_PREFIX}${path}`;
  if (params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => search.set(k, String(v)));
    url += `?${search.toString()}`;
  }

  // Build headers
  const reqHeaders: Record<string, string> = { ...headers };
  const token = tokenStorage.getAccessToken();
  if (token) {
    reqHeaders['Authorization'] = `Bearer ${token}`;
  }
  if (!raw && body) {
    reqHeaders['Content-Type'] = 'application/json';
  }

  // Make request
  let res = await fetch(url, {
    method,
    headers: reqHeaders,
    body: body
      ? raw
        ? (body as BodyInit)
        : JSON.stringify(body)
      : undefined,
  });

  // Handle 401 → try refresh
  if (res.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const refreshed = await (refreshPromise ?? Promise.resolve(false));

    if (refreshed) {
      // Retry original request with new token
      const newToken = tokenStorage.getAccessToken();
      if (newToken) reqHeaders['Authorization'] = `Bearer ${newToken}`;

      res = await fetch(url, {
        method,
        headers: reqHeaders,
        body: body
          ? raw
            ? (body as BodyInit)
            : JSON.stringify(body)
          : undefined,
      });
    } else {
      // Refresh failed — clear tokens and redirect
      tokenStorage.clear();
      window.location.href = '/login';
      throw new ApiError(401, 'Session expired');
    }
  }

  // Handle errors
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ detail: 'Unknown error' }));
    throw new ApiError(res.status, errorBody.detail ?? errorBody, errorBody.detail);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

// ── Public API ──────────────────────────────────────────────────────────

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, undefined, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};
